/* ============================================================
   useAccountSettings — OTAK HALAMAN SETTING
   ============================================================
   Semua keadaan, bacaan Firestore dan operasi Firebase Auth bagi
   halaman setting duduk di sini. TIADA JSX.

   Sebabnya: halaman setting wujud dalam DUA rupa —

     ProfileView.jsx      rupa lama  (suis ui_v39 MATI)
     SettingsV39View.jsx  rupa v3.9  (suis ui_v39 HIDUP)

   ⛔ JANGAN salin logik ini ke dalam mana-mana fail rupa. Ia
      operasi sensitif (tukar emel, tukar kata laluan, pautan
      akaun Google). Dua salinan bermakna satu daripadanya akan
      terlepas pembetulan pada suatu hari, dan kegagalannya senyap
      — pelanggan hilang akses kepada akaunnya sendiri. Rupa
      berbeza, otak satu.

   Nota corak yang WAJIB dikekalkan:
     · tukar emel guna verifyBeforeUpdateEmail (bukan updateEmail)
       — emel lama kekal berfungsi sehingga pautan diklik, jadi
       akaun tidak boleh terkunci
     · akaun Google-sahaja guna linkWithCredential untuk MENAMBAH
       kata laluan; UID kekal sama, jadi lesen/produk/kuota selamat
     · baca guna getDoc sekali, BUKAN onSnapshot — halaman ini
       jarang dibuka, listener realtime hanya membakar bacaan
   ============================================================ */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { doc, updateDoc, getDoc, collection, query, where, limit, getDocs, setDoc, getCountFromServer } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
    signOut, updatePassword, EmailAuthProvider, reauthenticateWithCredential,
    sendPasswordResetEmail, updateProfile, verifyBeforeUpdateEmail,
    linkWithCredential, GoogleAuthProvider, reauthenticateWithPopup,
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage, functions } from '../firebase';
import { getUserRole, ROLES } from '../utils/roles';
import { MODULES } from '../config/modules';
import { useT, cari } from '../i18n';

/* Had storage per pelanggan — WAJIB padan STORAGE_LIMIT_BYTES dalam App.jsx. */
export const STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024; // 5GB (naik dari 2GB — Fasa 3 R2)
/* Siling generation harian pelanggan berbayar — WAJIB padan
   DAILY_PAID_PIPELINE_CAP dalam functions/index.js. Tukar siling di satu
   tempat sahaja = angka yang dipapar tidak lagi selari dengan yang
   dikuatkuasakan server. */
export const DAILY_PAID_CAP = 100;
/* Kuota percubaan — one-shot, bukan harian. */
export const TRIAL_COIN_CAP = 5;
/* Kuota lalai bila lesen tiada nilai — WAJIB padan sandaran server
   (maxProfiles || 3 dalam validateLicense). */
export const DEFAULT_PROFILE_QUOTA = 3;
/* Admin/moderator dibenarkan 999 profil oleh server; papar sebagai
   'tanpa had' supaya nombor itu tidak dibaca sebagai kuota sebenar. */
export const ADMIN_PROFILE_QUOTA = 999;
/* Percubaan dikunci 1 slot browser DAN 1 avatar oleh server. */
export const TRIAL_PROFILE_QUOTA = 1;
/* Sandaran kuota AVATAR bila doc lesen tiada — WAJIB padan useState(5) pada
   avatarMaxSlots dalam App.jsx. Ia sengaja berbeza daripada sandaran slot
   browser (3); dua skrin itu memang menguatkuasakan nombor berlainan. */
export const AVATAR_FALLBACK_QUOTA = 5;

export const EXTENSION_ZIP_URL = '/SESAAT-EXTENSION.zip';

export const fmtBytes = (b) => {
    const bytes = Number(b) || 0;
    const mb = bytes / 1024 / 1024;
    if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
};

/* "Adakah versi v lebih rendah daripada req?" — juga dipakai untuk memilih
   versi TERTINGGI apabila lebih daripada satu extension menjawab. */
const _isVersionBelow = (v, req) => {
    if (!v) return true;                       // PONG tanpa versi = extension lama
    const pa = String(v).split('.').map(n => parseInt(n, 10) || 0);
    const pb = String(req).split('.').map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const a = pa[i] || 0, b = pb[i] || 0;
        if (a < b) return true;
        if (a > b) return false;
    }
    return false;
};

/* Probe extension — corak SAMA seperti WorkspaceView.pingExtension.
   Kumpul SEMUA PONG dahulu (jangan ambil yang pertama sampai): sejak v3.8.2
   menukar ID extension, pelanggan boleh ada DUA pemasangan serentak dan
   kedua-duanya menjawab. PONG extension lama tiada `version` — kalau ia
   menang perlumbaan, kita silap lapor "versi lama" walhal yang baharu
   memang terpasang. Itulah punca aduan "sy dah update, tapi masih keluar
   mcm ni" pada 2026-08-03. */
export const probeExtension = (collectMs = 300) => new Promise((resolve) => {
    let finished = false, hardTimer = null, collectTimer = null;
    const versions = [];
    const finish = () => {
        if (finished) return;
        finished = true;
        clearTimeout(hardTimer); clearTimeout(collectTimer);
        window.removeEventListener('message', handler);
        if (!versions.length) { resolve({ alive: false, version: '', responders: 0 }); return; }
        let best = versions[0];
        for (const v of versions) if (_isVersionBelow(best, v)) best = v;
        // Kira responden IKUT VERSI UNIK, bukan bilangan mesej — dua PING yang
        // bertindih menghasilkan dua PONG yang sama, itu bukan dua extension.
        const uniq = Array.from(new Set(versions));
        resolve({ alive: true, version: best, responders: uniq.length });
    };
    const handler = (event) => {
        if (finished || event.data?.type !== 'SESAAT_PONG') return;
        versions.push((event.data && event.data.version) || '');
        if (collectTimer) return;
        if (hardTimer) { clearTimeout(hardTimer); hardTimer = null; }
        collectTimer = setTimeout(finish, collectMs);
    };
    window.addEventListener('message', handler);
    try { window.postMessage({ type: 'SESAAT_PING' }, '*'); } catch { /* abai */ }
    hardTimer = setTimeout(finish, 1500);
});

/**
 * @param {object}  opts
 * @param {boolean} opts.enabled — set false untuk mod PELAN reka bentuk
 *   (`/settings-v3.9`). Bacaan Firestore dilangkau sepenuhnya; halaman itu
 *   memapar data contohnya sendiri. Tanpa suis ini, membuka pelan reka
 *   bentuk membakar bacaan doc pengguna tanpa sebab.
 */
/* `uiV39` — WAJIB dihantar oleh rupa v3.9 sahaja.
   Hook ini melahirkan 44 mesej operasi akaun (tukar kata laluan, tukar
   emel, avatar, nama, WhatsApp, log keluar) dan ia DIKONGSI oleh dua rupa:
   ProfileView (rupa LAMA — yang setiap pelanggan nampak hari ini, dan yang
   ayatnya semua Melayu bertulis tangan) dan SettingsV39View.
   Tanpa pagar ini, mesej-mesej itu keluar ENGLISH secara lalai di atas
   halaman berlabel Melayu — pada aliran akaun yang paling sensitif pula. */
export function useAccountSettings({ enabled = true, uiV39 = false } = {}) {
    const user = auth.currentUser;

    /* Mesej hasil operasi lahir DI SINI, bukan dalam fail rupa —
       jadi terjemahannya juga di sini. Kedua-dua rupa (ProfileView
       lama dan SettingsV39View) menerima teks yang sudah siap
       diterjemah, jadi kontrak `{type, text}` tidak berubah langsung
       dan tiada fail rupa perlu disentuh. */
    const tAktif = useT();
    /* Rupa lama dipaksa membaca kamus MELAYU terus, bukan bahasa aktif.
       Kamus ms.js memang menyimpan ayat ASAL hook ini, jadi rupa lama
       memapar perkataan yang sama seperti sebelum kerja dwibahasa —
       huruf demi huruf. */
    const t = useCallback(
        (kunci, vars) => (uiV39 ? tAktif(kunci, vars) : cari('ms', kunci, vars)),
        [uiV39, tAktif],
    );

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasLicenseKey, setHasLicenseKey] = useState(false);
    // 'smart' | 'monthly' | 'yearly' — dipakai untuk memapar laluan naik taraf RM100.
    const [licType, setLicType] = useState('');

    const [referrerInfo, setReferrerInfo] = useState(null);
    const [loadingReferrer, setLoadingReferrer] = useState(false);

    // Kata laluan
    const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });
    const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
    const [resetEmailLoading, setResetEmailLoading] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    // ★ Akaun Google-sahaja belum ada provider 'password' → kad tukar-kata-laluan
    // bertukar jadi kad TETAPKAN kata laluan buat kali pertama.
    const [hasPassword, setHasPassword] = useState(() =>
        (auth.currentUser?.providerData || []).some(p => p.providerId === 'password')
    );

    // Tema
    const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));

    // Sunting profil
    const [isEditingName, setIsEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [nameSaving, setNameSaving] = useState(false);
    const [nameMsg, setNameMsg] = useState({ type: '', text: '' });

    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [editEmail, setEditEmail] = useState('');
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailMsg, setEmailMsg] = useState({ type: '', text: '' });

    const [isEditingWhatsapp, setIsEditingWhatsapp] = useState(false);
    const [editWhatsapp, setEditWhatsapp] = useState('');
    const [whatsappSaving, setWhatsappSaving] = useState(false);
    const [whatsappMsg, setWhatsappMsg] = useState({ type: '', text: '' });

    // Pengesahan semula untuk operasi sensitif
    const [showReauthModal, setShowReauthModal] = useState(false);
    const [reauthPassword, setReauthPassword] = useState('');
    const [reauthLoading, setReauthLoading] = useState(false);
    const [reauthError, setReauthError] = useState('');
    const [reauthAction, setReauthAction] = useState(null);

    const [loggingOut, setLoggingOut] = useState(false);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const avatarInputRef = useRef(null);

    const [extState, setExtState] = useState({ status: 'idle', version: '', responders: 0 });

    /* Kuota slot + modul, dibaca daripada doc lesen. SATU nombor (maxProfiles)
       mengawal DUA benda: slot browser Chrome DAN slot avatar. Itu memang
       kelakuan sistem — App.jsx menetapkan avatarMaxSlots = licenses.maxProfiles.
       Jangan pisahkan di sini tanpa memisahkannya di App.jsx juga. */
    const [lesenKuota, setLesenKuota] = useState(null);      // nombor mentah drpd lesen
    const [modulDibenarkan, setModulDibenarkan] = useState([]);
    const [profilAktif, setProfilAktif] = useState([]);
    const [avatarDigunakan, setAvatarDigunakan] = useState(null);   // null = belum dikira

    // ===== BACA DATA =====
    useEffect(() => {
        if (!enabled) { setLoading(false); return; }
        if (!user) { setLoading(false); return; }
        const fetchData = async () => {
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                if (snap.exists()) setUserData(snap.data());
            } catch (e) {
                console.warn('[useAccountSettings] Gagal baca doc pengguna:', e);
            }
            try {
                const licSnap = await getDoc(doc(db, 'licenses', user.uid));
                if (licSnap.exists()) {
                    const d = licSnap.data();
                    setHasLicenseKey(!!(d.licenseKey && d.licenseKey.trim()));
                    setLicType(d.licType || 'monthly');
                    setLesenKuota(Number.isInteger(d.maxProfiles) ? d.maxProfiles : null);
                    setModulDibenarkan(Array.isArray(d.allowedModules) ? d.allowedModules : []);
                    setProfilAktif(Array.isArray(d.activeProfiles) ? d.activeProfiles : []);
                }
            } catch (e) { /* abai */ }
            setLoading(false);
        };
        fetchData();
    }, [user, enabled]);

    /* ===== BILANGAN AVATAR =====
       getCountFromServer, BUKAN getDocs — kita cuma perlu nombornya, dan
       avatar membawa data imej yang besar. Gagal baca → null, dan skrin
       memapar "—" dan bukan "0"; melaporkan 0 kepada pelanggan yang
       sebenarnya ada avatar adalah lebih buruk daripada tidak melapor. */
    useEffect(() => {
        if (!enabled || !user) return;
        let dibatalkan = false;
        (async () => {
            try {
                const snap = await getCountFromServer(collection(db, 'users', user.uid, 'avatars'));
                if (!dibatalkan) setAvatarDigunakan(snap.data().count);
            } catch (e) {
                console.warn('[useAccountSettings] Gagal kira avatar:', e?.message);
                if (!dibatalkan) setAvatarDigunakan(null);
            }
        })();
        return () => { dibatalkan = true; };
    }, [user, enabled]);

    // ===== EJEN RUJUKAN =====
    // Utama: users.referredBy (rekod rasmi)
    // Sandaran: cari referral doc where refereeId == uid (Buku 1 kosong, Buku 2 ada)
    // Baiki sendiri: kalau jumpa via sandaran, tulis balik ke users.referredBy
    useEffect(() => {
        if (!userData) { setReferrerInfo(null); return; }
        const uid = auth.currentUser?.uid;
        if (!uid) { setReferrerInfo(null); return; }

        let cancelled = false;
        setLoadingReferrer(true);

        const resolveAndSet = async (refCode) => {
            try {
                const fn = httpsCallable(functions, 'resolveAffiliateRef');
                const res = await fn({ ref: refCode });
                if (cancelled) return;
                setReferrerInfo(res.data?.found ? res.data : null);
            } catch {
                if (!cancelled) setReferrerInfo(null);
            } finally {
                if (!cancelled) setLoadingReferrer(false);
            }
        };

        if (userData.referredBy) {
            resolveAndSet(userData.referredBy);
            return () => { cancelled = true; };
        }

        (async () => {
            try {
                const q = query(collection(db, 'referrals'), where('refereeId', '==', uid), limit(1));
                const snap = await getDocs(q);
                if (cancelled) return;
                if (snap.empty) { setReferrerInfo(null); setLoadingReferrer(false); return; }
                const recoveredRefCode = snap.docs[0].data().refCode;
                if (!recoveredRefCode) { setReferrerInfo(null); setLoadingReferrer(false); return; }

                try {
                    await setDoc(doc(db, 'users', uid), { referredBy: recoveredRefCode }, { merge: true });
                    if (!cancelled) setUserData(prev => prev ? { ...prev, referredBy: recoveredRefCode } : prev);
                } catch (healErr) {
                    console.warn('[useAccountSettings] Baiki-sendiri referredBy gagal:', healErr?.message);
                }

                await resolveAndSet(recoveredRefCode);
            } catch (err) {
                console.warn('[useAccountSettings] Sandaran referral gagal:', err?.message);
                if (!cancelled) { setReferrerInfo(null); setLoadingReferrer(false); }
            }
        })();

        return () => { cancelled = true; };
    }, [userData]);

    // ===== PROBE EXTENSION =====
    const runExtProbe = useCallback(async () => {
        setExtState(s => ({ ...s, status: 'checking' }));
        const res = await probeExtension();
        setExtState({
            status: res.alive ? 'connected' : 'missing',
            version: res.version || '',
            responders: res.responders || 0,
        });
    }, []);

    // ===== TINDAKAN =====
    const handleLogout = async () => {
        setLoggingOut(true);
        try { await signOut(auth); }
        catch (error) { console.error('Logout error:', error); alert(t('acc.logoutFailed')); setLoggingOut(false); }
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !user) return;
        if (!file.type.startsWith('image/')) { alert(t('acc.pickImageOnly')); return; }
        if (file.size > 5 * 1024 * 1024) { alert(t('acc.fileTooBig')); return; }
        setAvatarPreview(URL.createObjectURL(file));
        setAvatarUploading(true);
        try {
            const storageRef = ref(storage, `avatars/${user.uid}`);
            // Path tetap (ditimpa bila tukar gambar) → cache pendek sahaja, jangan immutable.
            await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg', cacheControl: 'public, max-age=3600' });
            const downloadURL = await getDownloadURL(storageRef);
            await updateProfile(user, { photoURL: downloadURL });
            await updateDoc(doc(db, 'users', user.uid), { photoURL: downloadURL });
            setAvatarPreview(null);
        } catch (error) {
            console.error('Avatar upload error:', error); alert(t('acc.avatarFailed'));
            setAvatarPreview(null);
        } finally {
            setAvatarUploading(false);
            if (avatarInputRef.current) avatarInputRef.current.value = '';
        }
    };

    const handleRemoveAvatar = async () => {
        if (!user) return;
        setAvatarUploading(true);
        try {
            await updateProfile(user, { photoURL: '' });
            await updateDoc(doc(db, 'users', user.uid), { photoURL: '' });
            setAvatarPreview(null);
        } catch (error) { console.error('Remove avatar error:', error); }
        finally { setAvatarUploading(false); }
    };

    // ===== NAMA =====
    const startEditName = () => {
        setEditName(user?.displayName || user?.email?.split('@')[0] || '');
        setNameMsg({ type: '', text: '' });
        setIsEditingName(true);
    };

    const handleSaveName = async () => {
        if (!editName.trim()) { setNameMsg({ type: 'error', text: t('acc.nameEmpty') }); return; }
        if (editName.trim() === (user?.displayName || user?.email?.split('@')[0])) { setIsEditingName(false); return; }
        setNameSaving(true);
        setNameMsg({ type: '', text: '' });
        try {
            await updateProfile(user, { displayName: editName.trim() });
            await updateDoc(doc(db, 'users', user.uid), { displayName: editName.trim() });
            setNameMsg({ type: 'success', text: t('acc.nameUpdated') });
            setIsEditingName(false);
            setTimeout(() => setNameMsg({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('Name update error:', error);
            setNameMsg({ type: 'error', text: t('acc.nameFailed', { ralat: error.message }) });
        } finally { setNameSaving(false); }
    };

    const cancelEditName = () => { setIsEditingName(false); setNameMsg({ type: '', text: '' }); };

    // ===== WHATSAPP (nombor akaun SESAAT — disimpan pada doc users) =====
    const startEditWhatsapp = () => {
        setEditWhatsapp(userData?.whatsappNumber || '');
        setWhatsappMsg({ type: '', text: '' });
        setIsEditingWhatsapp(true);
    };

    const handleSaveWhatsapp = async () => {
        const cleaned = String(editWhatsapp || '').replace(/\D/g, '');
        if (cleaned.length < 9 || cleaned.length > 15) {
            setWhatsappMsg({ type: 'error', text: t('acc.waInvalid') });
            return;
        }
        let normalized = cleaned;
        if (cleaned.startsWith('60')) normalized = `+${cleaned}`;
        else if (cleaned.startsWith('0')) normalized = `+60${cleaned.slice(1)}`;
        else normalized = `+60${cleaned}`;

        if (normalized === (userData?.whatsappNumber || '')) { setIsEditingWhatsapp(false); return; }
        setWhatsappSaving(true);
        setWhatsappMsg({ type: '', text: '' });
        try {
            await updateDoc(doc(db, 'users', user.uid), { whatsappNumber: normalized });
            setUserData(prev => prev ? { ...prev, whatsappNumber: normalized } : prev);
            setWhatsappMsg({ type: 'success', text: t('acc.waUpdated') });
            setIsEditingWhatsapp(false);
            setTimeout(() => setWhatsappMsg({ type: '', text: '' }), 3000);
        } catch (error) {
            console.error('WhatsApp update error:', error);
            setWhatsappMsg({ type: 'error', text: t('acc.waFailed', { ralat: error.message }) });
        } finally { setWhatsappSaving(false); }
    };

    const cancelEditWhatsapp = () => { setIsEditingWhatsapp(false); setWhatsappMsg({ type: '', text: '' }); };

    // ===== EMEL =====
    const startEditEmail = () => {
        setEditEmail(user?.email || '');
        setEmailMsg({ type: '', text: '' });
        setIsEditingEmail(true);
    };

    const handleSaveEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!editEmail.trim() || !emailRegex.test(editEmail.trim())) {
            setEmailMsg({ type: 'error', text: t('acc.emailInvalid') });
            return;
        }
        if (editEmail.trim().toLowerCase() === user?.email?.toLowerCase()) { setIsEditingEmail(false); return; }
        // Tukar emel = operasi sensitif → sahkan identiti dahulu.
        setReauthAction('email');
        setReauthPassword('');
        setReauthError('');
        setShowReauthModal(true);
    };

    const cancelEditEmail = () => { setIsEditingEmail(false); setEmailMsg({ type: '', text: '' }); };

    const closeReauthModal = () => { setShowReauthModal(false); setReauthPassword(''); setReauthError(''); };

    const handleReauthAndExecute = async () => {
        if (!reauthPassword) { setReauthError(t('acc.reauthEnterPw')); return; }
        setReauthLoading(true);
        setReauthError('');
        try {
            const credential = EmailAuthProvider.credential(user.email, reauthPassword);
            await reauthenticateWithCredential(user, credential);

            if (reauthAction === 'email') {
                setEmailSaving(true);
                try {
                    // ★ actionCodeSettings memberitahu Firebase ke mana pelanggan dihantar
                    // selepas mengesahkan emel. WAJIB domain production — localhost bukan
                    // domain yang dibenarkan untuk emel tindakan Firebase.
                    const actionCodeSettings = {
                        url: 'https://app.sesaat.ai/profile/account',
                        handleCodeInApp: false,
                    };
                    await verifyBeforeUpdateEmail(user, editEmail.trim(), actionCodeSettings);
                    setEmailMsg({
                        type: 'success',
                        text: `Emel pengesahan telah dihantar ke ${editEmail.trim()}. Sila semak peti masuk anda (termasuk folder spam) untuk mengesahkan perubahan.`,
                    });
                    setIsEditingEmail(false);
                } catch (emailError) {
                    console.error('[useAccountSettings] Email update error:', emailError?.code, emailError?.message);
                    if (emailError.code === 'auth/email-already-in-use') {
                        setEmailMsg({ type: 'error', text: t('acc.emailInUse') });
                    } else if (emailError.code === 'auth/invalid-email') {
                        setEmailMsg({ type: 'error', text: t('acc.emailBad') });
                    } else if (emailError.code === 'auth/requires-recent-login') {
                        setEmailMsg({ type: 'error', text: t('acc.sessionExpiredRetry') });
                    } else {
                        setEmailMsg({ type: 'error', text: t('acc.emailFailed', { ralat: emailError.message || t('acc.unknownError') }) });
                    }
                } finally { setEmailSaving(false); }
            }

            setShowReauthModal(false);
            setReauthPassword('');
        } catch (error) {
            console.error('Re-auth error:', error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                setReauthError(t('acc.reauthWrongPw'));
            } else if (error.code === 'auth/too-many-requests') {
                setReauthError(t('acc.tooManyAttempts'));
            } else {
                setReauthError(t('acc.reauthFailed', { ralat: error.message }));
            }
        } finally { setReauthLoading(false); }
    };

    const handleChangePassword = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setPasswordMsg({ type: '', text: '' });
        if (passwordData.new !== passwordData.confirm) {
            setPasswordMsg({ type: 'error', text: t('acc.pwMismatchNew') }); return;
        }
        if (passwordData.new.length < 6) {
            setPasswordMsg({ type: 'error', text: t('acc.pwTooShortNew') }); return;
        }
        setPasswordLoading(true);
        try {
            const credential = EmailAuthProvider.credential(user.email, passwordData.current);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, passwordData.new);
            setPasswordMsg({ type: 'success', text: t('acc.pwUpdated') });
            setPasswordData({ current: '', new: '', confirm: '' });
            setShowPasswords({ current: false, new: false, confirm: false });
            setTimeout(() => setPasswordMsg({ type: '', text: '' }), 4000);
        } catch (error) {
            console.error('Password change error:', error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential')
                setPasswordMsg({ type: 'error', text: t('acc.pwCurrentWrong') });
            else if (error.code === 'auth/too-many-requests')
                setPasswordMsg({ type: 'error', text: t('acc.tooManyAttempts') });
            else if (error.code === 'auth/requires-recent-login')
                setPasswordMsg({ type: 'error', text: t('acc.sessionExpired') });
            else setPasswordMsg({ type: 'error', text: t('acc.pwChangeFailed', { ralat: error.message }) });
        } finally { setPasswordLoading(false); }
    };

    // ★ AKAUN GOOGLE-SAHAJA: tetapkan kata laluan buat kali pertama.
    // linkWithCredential menambah provider 'password' pada akaun SEDIA ADA — UID
    // kekal sama, jadi lesen/produk/kuota langsung tidak terjejas. Selepas ini
    // pelanggan boleh log masuk webapp DAN extension guna emel + kata laluan
    // (penting: 3-5 browser satu lesen, tidak semua browser ada sesi Gmail).
    const handleSetPassword = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setPasswordMsg({ type: '', text: '' });
        if (passwordData.new !== passwordData.confirm) {
            setPasswordMsg({ type: 'error', text: t('acc.pwMismatch') }); return;
        }
        if (passwordData.new.length < 6) {
            setPasswordMsg({ type: 'error', text: t('acc.pwTooShort') }); return;
        }
        if (!user?.email) {
            setPasswordMsg({ type: 'error', text: t('acc.noEmail') }); return;
        }
        setPasswordLoading(true);
        try {
            const credential = EmailAuthProvider.credential(user.email, passwordData.new);
            try {
                await linkWithCredential(user, credential);
            } catch (err) {
                // Firebase menolak operasi sensitif kalau sesi log masuk sudah lama.
                // Sahkan semula guna Google (satu-satunya cara masuk pelanggan ini
                // ada), lepas tu ulang.
                if (err.code === 'auth/requires-recent-login') {
                    await reauthenticateWithPopup(user, new GoogleAuthProvider());
                    await linkWithCredential(user, credential);
                } else throw err;
            }
            setHasPassword(true);
            setPasswordData({ current: '', new: '', confirm: '' });
            setShowPasswords({ current: false, new: false, confirm: false });
            setPasswordMsg({
                type: 'success',
                text: `Kata laluan telah ditetapkan. Anda kini boleh log masuk menggunakan ${user.email} dan kata laluan ini pada mana-mana browser, termasuk Extension SESAAT.`,
            });
        } catch (error) {
            console.error('Set password error:', error);
            if (error.code === 'auth/provider-already-linked') {
                setHasPassword(true);
                setPasswordMsg({ type: 'error', text: t('acc.alreadyHasPw') });
            }
            else if (error.code === 'auth/credential-already-in-use' || error.code === 'auth/email-already-in-use')
                setPasswordMsg({ type: 'error', text: t('acc.emailInUseAdmin') });
            else if (error.code === 'auth/weak-password')
                setPasswordMsg({ type: 'error', text: t('acc.pwTooWeak') });
            else if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request')
                setPasswordMsg({ type: 'error', text: t('acc.googleCancelled') });
            else if (error.code === 'auth/popup-blocked')
                setPasswordMsg({ type: 'error', text: t('acc.googleBlocked') });
            else if (error.code === 'auth/requires-recent-login')
                setPasswordMsg({ type: 'error', text: t('acc.sessionExpiredGoogle') });
            else setPasswordMsg({ type: 'error', text: t('acc.pwSetFailed', { ralat: error.message }) });
        } finally { setPasswordLoading(false); }
    };

    const handleResetPasswordEmail = async () => {
        if (!user?.email) return;
        setResetEmailLoading(true);
        setPasswordMsg({ type: '', text: '' });
        try {
            await sendPasswordResetEmail(auth, user.email);
            setResetEmailSent(true);
            setPasswordMsg({ type: 'success', text: `Emel tetapan semula telah dihantar ke ${user.email}.` });
        } catch (error) {
            console.error('Reset email error:', error);
            if (error.code === 'auth/too-many-requests')
                setPasswordMsg({ type: 'error', text: t('acc.tooManyRequests') });
            else setPasswordMsg({ type: 'error', text: t('acc.resetEmailFailed', { ralat: error.message }) });
        } finally { setResetEmailLoading(false); }
    };

    // ===== TEMA =====
    const handleThemeChange = (mode) => {
        if (mode === 'dark') {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
            setDarkMode(true);
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
            setDarkMode(false);
        }
    };

    // ===== NILAI TERBITAN =====
    const userRole = getUserRole(user?.email);
    const tier = userRole === ROLES.ADMIN ? 'admin'
        : userRole === ROLES.MODERATOR ? 'moderator'
            : hasLicenseKey ? 'paid'
                : (userData?.tier || 'free');
    const isAdmin = tier === 'admin';
    const isPaid = tier === 'paid' || isAdmin || tier === 'moderator';
    const plan = userData?.plan;
    const paidExpiry = userData?.paidExpiry;
    const createdAt = userData?.createdAt;

    const initials = user?.email ? user.email.substring(0, 2).toUpperCase() : '??';
    const currentPhotoURL = avatarPreview || user?.photoURL || userData?.photoURL || null;
    const displayName = user?.displayName || user?.email?.split('@')[0] || 'Pengguna';

    const joinedDate = createdAt?.toDate
        ? createdAt.toDate().toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })
        : user?.metadata?.creationTime
            ? new Date(user.metadata.creationTime).toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })
            : 'Tiada rekod';

    const expiryDate = paidExpiry
        ? new Date(paidExpiry.seconds ? paidExpiry.seconds * 1000 : paidExpiry)
            .toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' })
        : null;

    // Kaunter storage dibaca terus daripada doc pengguna (BUKAN imbasan bucket) —
    // imbasan penuh pernah menjadi punca kos Storage operations paling besar.
    const storageUsed = Number(userData?.storageUsedBytes) || 0;
    const storageRatio = storageUsed / STORAGE_LIMIT_BYTES;

    // Kaunter harian reset pada tengah malam waktu Malaysia. Kalau tarikh pada doc
    // bukan tarikh KL hari ini, kaunter itu basi → kira sebagai sifar.
    const klToday = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const usedToday = userData?.dailyPipelineDate === klToday ? (Number(userData?.dailyPipelineUsed) || 0) : 0;
    const trialCoins = userData?.pipelineCoins ?? null;

    /* Kuota slot yang BERKUATKUASA — dikira dengan peraturan yang SAMA seperti
       server (validateLicense) dan App.jsx, mengikut turutan yang sama:
         admin/moderator → tanpa had
         percubaan tanpa lesen → 1
         selain itu → licenses.maxProfiles, sandaran 3
       Kalau turutan ini terpesong daripada server, pelanggan akan nampak satu
       nombor di sini dan tersekat pada nombor yang lain. */
    const stafTanpaHad = isAdmin || tier === 'moderator';
    const kuotaSlot = stafTanpaHad
        ? ADMIN_PROFILE_QUOTA
        : (tier === 'trial' && lesenKuota === null)
            ? TRIAL_PROFILE_QUOTA
            : (lesenKuota ?? DEFAULT_PROFILE_QUOTA);
    const slotTanpaHad = stafTanpaHad;
    const browserDigunakan = profilAktif.length;

    /* ⚠️ Slot avatar TIDAK mengikut peraturan yang sama dengan slot browser.
       App.jsx (yang menguatkuasakannya) membaca licenses.maxProfiles terus,
       dengan sandaran 5 — ia TIDAK memberi laluan pintas kepada admin. Jadi
       admin tanpa doc lesen sebenarnya terhad kepada 5 avatar walaupun slot
       browsernya tanpa had. Kita papar apa yang BENAR-BENAR dikuatkuasakan;
       memapar "tanpa had" di sini akan menyalahi skrin avatar itu sendiri. */
    const avatarKuota = tier === 'trial'
        ? TRIAL_PROFILE_QUOTA
        : (lesenKuota ?? AVATAR_FALLBACK_QUOTA);

    /* Modul yang pelanggan betul-betul ada, ditukar kepada nama pemasaran.
       Modul yang tidak dikenali digugurkan — memapar id mentah seperti
       'ai16saat-half' kepada pelanggan hanya menimbulkan soalan. */
    const modulSaya = useMemo(
        () => modulDibenarkan
            .map((id) => ({ id, nama: MODULES[id]?.marketingName }))
            .filter((m) => !!m.nama),
        [modulDibenarkan],
    );

    const passwordStrength = useMemo(() => {
        const pw = passwordData.new;
        if (!pw) return { score: 0, label: '', pct: 0 };
        let score = 0;
        if (pw.length >= 6) score++;
        if (pw.length >= 8) score++;
        if (/[A-Z]/.test(pw)) score++;
        if (/[0-9]/.test(pw)) score++;
        if (/[^A-Za-z0-9]/.test(pw)) score++;
        const labels = ['', t('acc.pwStrength1'), t('acc.pwStrength2'), t('acc.pwStrength3'), t('acc.pwStrength4'), t('acc.pwStrength5')];
        return { score, label: labels[score], pct: score * 20 };
    }, [passwordData.new, t]);

    const passwordValidation = useMemo(() => {
        const pw = passwordData.new;
        return {
            minLength: pw.length >= 6,
            matches: !!(pw && passwordData.confirm && pw === passwordData.confirm),
            mismatch: !!(pw && passwordData.confirm && pw !== passwordData.confirm),
        };
    }, [passwordData.new, passwordData.confirm]);

    /* Boleh tekan butang simpan kata laluan? Dikira di sini supaya kedua-dua
       rupa mengenakan syarat yang SAMA — kalau salah satu lebih longgar,
       pelanggan pada rupa itu boleh menghantar borang yang pasti gagal. */
    const canSubmitPassword = !passwordLoading
        && !(hasPassword && !passwordData.current)
        && passwordValidation.minLength
        && !passwordValidation.mismatch;

    return {
        user, userData, loading,
        // identiti
        tier, isAdmin, isPaid, plan, licType, displayName, initials, currentPhotoURL,
        joinedDate, expiryDate,
        // storage & kuota
        storageUsed, storageRatio, usedToday, trialCoins,
        // slot browser + avatar + modul
        kuotaSlot, slotTanpaHad, browserDigunakan, avatarKuota, avatarDigunakan, modulSaya,
        // ejen rujukan
        referrerInfo, loadingReferrer,
        // extension
        extState, runExtProbe,
        // tema
        darkMode, handleThemeChange,
        // avatar
        avatarInputRef, avatarUploading, handleAvatarUpload, handleRemoveAvatar,
        // nama
        isEditingName, editName, setEditName, nameSaving, nameMsg,
        startEditName, handleSaveName, cancelEditName,
        // emel
        isEditingEmail, editEmail, setEditEmail, emailSaving, emailMsg,
        startEditEmail, handleSaveEmail, cancelEditEmail,
        // whatsapp
        isEditingWhatsapp, editWhatsapp, setEditWhatsapp, whatsappSaving, whatsappMsg,
        startEditWhatsapp, handleSaveWhatsapp, cancelEditWhatsapp,
        // kata laluan
        hasPassword, passwordData, setPasswordData, showPasswords, setShowPasswords,
        passwordLoading, passwordMsg, passwordStrength, passwordValidation, canSubmitPassword,
        handleChangePassword, handleSetPassword,
        resetEmailLoading, resetEmailSent, handleResetPasswordEmail,
        // pengesahan semula
        showReauthModal, reauthPassword, setReauthPassword, reauthLoading, reauthError,
        reauthAction, handleReauthAndExecute, closeReauthModal,
        // log keluar
        loggingOut, handleLogout,
    };
}
